import { Alert, Button } from '@mantine/core'
import { IconAlertCircle, IconDeviceFloppy } from '@tabler/icons-react'
import { PRIMARY_BUTTON_PROPS } from './primaryButton.tokens'

/** The mark a tab label carries while its form holds unsaved edits. */
export function DirtyDot() {
  return (
    <span
      role="img"
      aria-label="Unsaved changes"
      className="inline-block h-2 w-2 rounded-full bg-[#E5533D]"
    />
  )
}

/**
 * One tab form's Save (and Cancel, when the tab offers one) with the inline alert for a
 * failed save. Save is disabled while the form is clean and shows its own loading state;
 * shared by Provider Operations and My Profile.
 */
export function SaveFooter({
  saveError,
  isSaving,
  isDirty,
  held = false,
  onCancel,
}: {
  saveError: { title: string; message: string } | null
  isSaving: boolean
  isDirty: boolean
  // Another tab's save is in flight: Profile and Licenses each re-send the sibling as
  // last fetched, so two overlapping writes could re-apply a value the other just saved.
  held?: boolean
  /** Puts the last saved values back; the button only shows when given. */
  onCancel?: () => void
}) {
  return (
    // Pinned to the bottom of the viewport while the tab is taller than the screen (a long
    // Licenses list), flush with the card's edges; the negative margins undo the card's
    // padding. Outside the tab's <fieldset>: the button's own loading state disables it.
    <div className="sticky bottom-0 z-10 -mx-6 mt-4 -mb-6 flex flex-col gap-3 rounded-b-[18px] border-t border-[#E7E9EC] bg-white px-6 py-4">
      {saveError && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} title={saveError.title}>
          {saveError.message}
        </Alert>
      )}
      <div className="flex justify-end gap-2">
        {onCancel && (
          <Button variant="default" onClick={onCancel} disabled={!isDirty || isSaving}>
            Cancel
          </Button>
        )}
        <Button
          {...PRIMARY_BUTTON_PROPS}
          leftSection={<IconDeviceFloppy size={16} />}
          type="submit"
          loading={isSaving}
          disabled={!isDirty || held}
        >
          Save
        </Button>
      </div>
    </div>
  )
}
